import { useEffect } from 'react'
import { useSettings } from './useSettings'

const THEME_STORAGE_KEY = 'cc-theme'

// Get system preference
function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'dark'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

// Resolve theme to actual light/dark
function resolveTheme(theme: string): 'light' | 'dark' {
  if (theme === 'system') {
    return getSystemTheme()
  }
  return theme === 'light' ? 'light' : 'dark'
}

// Apply theme class to document
function applyTheme(theme: 'light' | 'dark') {
  const root = document.documentElement
  root.classList.remove('light', 'dark')
  root.classList.add(theme)

  // Also sync to localStorage for flash prevention script
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme)
  } catch {
    // Ignore storage errors
  }
}

// Hook to apply theme based on settings
export function useThemeEffect() {
  const { settings, loading } = useSettings()
  const theme = settings?.theme ?? 'dark'

  useEffect(() => {
    // Don't apply during initial load - the flash prevention script handles that
    if (loading) return

    const resolved = resolveTheme(theme)
    applyTheme(resolved)
  }, [theme, loading])

  // Listen for system preference changes when using 'system' theme
  useEffect(() => {
    if (theme !== 'system') return

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    function handleChange(e: MediaQueryListEvent) {
      applyTheme(e.matches ? 'dark' : 'light')
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [theme])
}

// Sync localStorage when settings change (for flash prevention)
export function syncThemeToStorage(theme: string) {
  const resolved = resolveTheme(theme)
  try {
    localStorage.setItem(THEME_STORAGE_KEY, resolved)
  } catch {
    // Ignore storage errors
  }
}
